import React from 'react';
import { Loader, Text } from '@vibe/core';
import { useCurrentUserAccess } from '../../hooks/useCurrentUserAccess';
import { useMondayContext } from '../../context/MondayContext';

/**
 * Wraps any view surface and blocks rendering if the user is a viewer,
 * restricted, or has no access.
 * Admins and regular users see the children; viewers/restricted/no-access users see a message.
 */
const AccessGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { role, hasAccess, isLoading, error } = useCurrentUserAccess();
  const { isViewer, isReady } = useMondayContext();

  if (isLoading || !isReady) {
    return (
      <div className="rp-view-loading">
        <Loader size={Loader.sizes.MEDIUM} />
      </div>
    );
  }

  if (isViewer) {
    return (
      <div className="rp-view-empty">
        <h3>Viewer Access Not Supported</h3>
        <p>
          As a viewer, you are unable to use WorkHub. Please contact your workspace
          administrator to upgrade your account to a member role.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rp-view-error">
        <Text type={Text.types.TEXT1} weight={Text.weights.BOLD}>
          Unable to verify access
        </Text>
        <Text type={Text.types.TEXT2}>{error}</Text>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="rp-view-empty">
        <h3>Access Restricted</h3>
        <p>
          {role === 'restricted'
            ? 'Your access to WorkHub has been restricted. Please contact your administrator.'
            : 'You do not have access to WorkHub. Please contact your administrator to get access.'}
        </p>
      </div>
    );
  }

  return <>{children}</>;
};

export default AccessGate;
